/**
 * Gestion des participants + quantités par plan pour le checkout adhésion /a/[slug].
 * Sort le bloc participants/quantity du composant index.vue pour réduire la taille.
 */
import { tarifAdhesionFerme } from "~/utils/tarifAdhesion";
import type {
  MembershipPlanPublic,
  MembershipPlanOption,
  MembershipCustomField,
} from "~/types/membership";

export interface ParticipantLocal {
  planId: string;
  planTitle: string;
  planPrice: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  selectedOptions: Record<string, boolean>;
  customFieldValues: Record<string, unknown>;
}

type SiblingPlan = MembershipPlanPublic & {
  options?: MembershipPlanOption[];
  custom_fields?: MembershipCustomField[];
};

const MAX_QUANTITY_PER_PLAN = 10;

export function useMembershipParticipants(opts: {
  plan: Ref<MembershipPlanPublic | null>;
  allPlans: Ref<SiblingPlan[]>;
  hasMultiplePlans: Ref<boolean>;
  remainingPlaces: Ref<number | null>;
  getPlanOptions: (planId: string) => MembershipPlanOption[];
  getPlanTitle: (planId: string) => string;
  getPlanPrice: (planId: string) => number;
}) {
  const planQuantities = ref<Record<string, number>>({});
  const payerIndex = ref(0);

  // Tarif mis à 1 par la page (pas par la personne) : retiré si d'autres
  // tarifs apparaissent. `null` dès que la personne touche aux quantités.
  let preselectionAuto: string | null = null;

  watch([opts.plan, opts.hasMultiplePlans], ([p, plusieurs]) => {
    // PLUSIEURS tarifs : aucun n'est choisi d'avance, c'est à la personne de
    // choisir le sien (Karim 2026-10-29 — le 1er tarif était coché d'office,
    // comme en billetterie il faut partir de 0). Les frères arrivent après le
    // tarif : une présélection faite avant leur arrivée est retirée.
    if (plusieurs) {
      if (preselectionAuto && planQuantities.value[preselectionAuto] === 1) {
        delete planQuantities.value[preselectionAuto];
      }
      preselectionAuto = null;
      return;
    }
    // UN SEUL tarif : rien à choisir, le compteur part de 1.
    // Ne PAS pré-sélectionner un tarif clôturé (non achetable) ni un tarif
    // dont la saison est terminée (Karim 2026-06-27, 2026-10-09).
    if (p && !tarifAdhesionFerme(p) && !planQuantities.value[p.id]) {
      planQuantities.value[p.id] = 1;
      preselectionAuto = p.id;
    }
  }, { immediate: true });

  // Panier repris (retour sur la page, `?step=…`) : c'est un choix de la
  // personne, on n'y touche plus.
  watch(planQuantities, () => {
    preselectionAuto = null;
  });

  const quantity = computed(() => {
    let total = 0;
    for (const q of Object.values(planQuantities.value)) total += q;
    return total;
  });

  const maxQuantityPerPlan = MAX_QUANTITY_PER_PLAN;

  function incrementPlanQuantity(planId: string) {
    preselectionAuto = null;
    const current = planQuantities.value[planId] || 0;
    if (current < maxQuantityPerPlan) planQuantities.value[planId] = current + 1;
  }

  function decrementPlanQuantity(planId: string) {
    preselectionAuto = null;
    const current = planQuantities.value[planId] || 0;
    if (current > 0) planQuantities.value[planId] = current - 1;
  }

  const maxQuantity = computed(() => {
    const hardMax = 10;
    if (opts.remainingPlaces.value !== null) return Math.min(hardMax, opts.remainingPlaces.value);
    return hardMax;
  });

  function incrementQuantity() {
    if (opts.plan.value) incrementPlanQuantity(opts.plan.value.id);
  }

  function decrementQuantity() {
    if (opts.plan.value) {
      const current = planQuantities.value[opts.plan.value.id] || 0;
      if (current > 1) planQuantities.value[opts.plan.value.id] = current - 1;
    }
  }

  function createEmptyParticipant(planId?: string): ParticipantLocal {
    const pid = planId || opts.plan.value?.id || '';
    const selectedOptions: Record<string, boolean> = {};
    const pOptions = opts.getPlanOptions(pid);
    for (const opt of pOptions) {
      if (opt.is_required && opt.is_active) selectedOptions[opt.id] = true;
    }
    return {
      planId: pid,
      planTitle: opts.getPlanTitle(pid),
      planPrice: opts.getPlanPrice(pid),
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      selectedOptions,
      customFieldValues: {},
    };
  }

  const participants = ref<ParticipantLocal[]>([createEmptyParticipant()]);
  const firstParticipant = computed(() => participants.value[0]!);

  function buildParticipantsList() {
    if (opts.hasMultiplePlans.value) {
      const newParticipants: ParticipantLocal[] = [];
      for (const ap of opts.allPlans.value) {
        const qty = planQuantities.value[ap.id] || 0;
        for (let i = 0; i < qty; i++) {
          const existingForPlan = participants.value.filter(p => p.planId === ap.id);
          if (existingForPlan[i]) {
            newParticipants.push(existingForPlan[i]!);
          } else {
            newParticipants.push(createEmptyParticipant(ap.id));
          }
        }
      }
      participants.value = newParticipants;
    } else {
      const currentCount = participants.value.length;
      const targetCount = quantity.value;
      if (targetCount > currentCount) {
        for (let i = currentCount; i < targetCount; i++) participants.value.push(createEmptyParticipant());
      } else if (targetCount < currentCount) {
        participants.value.splice(targetCount);
      }
    }
    if (payerIndex.value >= participants.value.length) payerIndex.value = 0;
  }

  return {
    planQuantities,
    payerIndex,
    quantity,
    maxQuantity,
    maxQuantityPerPlan,
    participants,
    firstParticipant,
    incrementPlanQuantity,
    decrementPlanQuantity,
    incrementQuantity,
    decrementQuantity,
    createEmptyParticipant,
    buildParticipantsList,
  };
}
